import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicUrl = (path) => `${BACKEND}/api/public/files/serve/${path}`;

/**
 * EventGallery — horizontal carousel of syndicated event photos.
 * Used by /school/:slug and (compact variant) by the root landing GlobalLanding.
 */
export default function EventGallery({slug, compact = false, embedded = false}) {
  const [rows, setRows] = useState({school: null, photos: []});
  const scrollerRef = useRef(null);
  useEffect(() => {
    if (!slug) return;
    axios.get(`${BACKEND}/api/public/schools/${slug}/event-media`)
      .then(r => setRows(r.data))
      .catch(() => setRows({school: null, photos: []}));
  }, [slug]);
  const scrollBy = (dx) => scrollerRef.current?.scrollBy({left: dx, behavior: "smooth"});
  if (!rows.photos || rows.photos.length === 0) {
    if (embedded) return null;
    return <div className="max-w-7xl mx-auto px-6 py-8 text-center text-slate-500">
      No campus event highlights published yet.
    </div>;
  }
  const cardW = compact ? 180 : 300;
  const cardH = compact ? 120 : 200;
  return <section className={`${embedded ? "" : "max-w-7xl mx-auto px-6 py-10"}`}
                    data-testid={`event-gallery-${slug}`}>
    {!embedded && (
      <div className="mb-4 flex items-end justify-between">
        <div>
          <div className="nb-label">FROM {rows.school?.name?.toUpperCase() || "CAMPUS"}</div>
          <h2 className="display text-3xl font-black">Campus Event Highlights</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>scrollBy(-cardW*2)}
                  className="nb-btn nb-btn-secondary p-2"
                  data-testid="gallery-prev">
            <ChevronLeft size={18}/>
          </button>
          <button onClick={()=>scrollBy(cardW*2)}
                  className="nb-btn nb-btn-secondary p-2"
                  data-testid="gallery-next">
            <ChevronRight size={18}/>
          </button>
        </div>
      </div>
    )}
    <div className="relative">
      {embedded && rows.photos.length > 2 && (
        <>
          <button onClick={(e)=>{e.stopPropagation(); scrollBy(-cardW*2);}}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 border-2 border-black rounded-full p-1 shadow"
                  data-testid={`gallery-embed-prev-${slug}`}>
            <ChevronLeft size={16}/>
          </button>
          <button onClick={(e)=>{e.stopPropagation(); scrollBy(cardW*2);}}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 border-2 border-black rounded-full p-1 shadow"
                  data-testid={`gallery-embed-next-${slug}`}>
            <ChevronRight size={16}/>
          </button>
        </>
      )}
      <div ref={scrollerRef}
           className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
           style={{scrollbarWidth: "thin"}}>
        {rows.photos.map(p => (
          <a key={p.media_id} href={publicUrl(p.storage_path)}
             target="_blank" rel="noreferrer"
             className="flex-shrink-0 snap-start border-2 border-black rounded shadow-[4px_4px_0_0_#000] overflow-hidden bg-white hover:shadow-[6px_6px_0_0_#000] transition-shadow"
             style={{width: cardW}}
             data-testid={`gallery-photo-${p.media_id}`}>
            <img src={publicUrl(p.storage_path)}
                 alt={p.caption}
                 style={{height: cardH, width: "100%", objectFit: "cover"}}
                 loading="lazy"/>
            {!compact && (
              <div className="p-2">
                <div className="font-bold text-sm truncate">{p.event_name}</div>
                <div className="text-[10px] text-slate-500">{p.event_date}</div>
              </div>
            )}
          </a>
        ))}
      </div>
    </div>
  </section>;
}
